/* Chase & request — LETTERS FIRST (Tier 2 · D2, Ale ruling 2026-09-05;
 * mock ux-audit-2026-09-04/mocks/chase-letters-first).
 *
 * The queue is the letters. A fund-level contact strip says once who the
 * chases go to and who the counterparty is; three tabs hold the whole of
 * the work — Letters (ready to send) · Still to request (nothing asked
 * for yet) · Sent (already gone out). One card = one counterparty = ONE
 * letter, with ONE LINE PER OWED DOCUMENT and a single verb
 * "Send — n documents". Every owed document sits in exactly one tab, so
 * the counts in the tab bar are the counts in the lists.
 *
 * COPY PATH (Ale ruling 2026-09-04, kept whole): every owed document is
 * one line rendered from structured data — demand code chip + plain name
 * · the object in its canon colour · accepted-source chips · owed age.
 * The engine's task DESCRIPTION is never printed (it carries catalogue
 * prose, ruling citations and enum words); the title is parsed by
 * lib/chaseCopy, dates print in the one officer style, a non-mandatory
 * demand carries a small "optional" tag, and a counterparty the write
 * path stored as "Unknown" becomes a door.
 *
 * PLUGS (same ruling, kept whole): a row whose object is an officer plug
 * trade is tagged — MISSING MOVEMENT rows stay in the list with the
 * booking line; INCEPTION BALANCE rows are held back in a collapsed
 * group with no Draft/Send verb (the engine still mints them; the queue
 * does not send them — a starting line never goes to the counterparty).
 *
 * Request states print in the ruled vocabulary (2026-08-31):
 * drafted · requested <date> · overdue — reminder due · escalated ·
 * fulfilled. */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import {
  ageDays,
  demandName,
  normaliseCounterparty,
  objectCell,
  parseChaseTitle,
  plugKind,
  plugLine,
  sourceChips,
  tradeParts,
  type ObjectCell,
  type PlugKind,
} from '../../lib/chaseCopy'
import { formatOfficerDate } from '../../lib/officerFormat'
import { fetchOpenTasks } from './api'
import { fetchFundLedgers } from '../ledger'
import { sumLedgers, type FundLedger } from '../ledgerWords'
import { EmptyState, Skeleton } from '../state'
import '../canon.css'

interface DocRequest {
  id: string
  fund_id: string
  counterparty_name: string
  status: string
  deadline?: string | null
  sent_at?: string | null
  evidence_status_id?: string | null
  recipient_email?: string | null
  created_at: string
}

interface FundLite {
  id: string
  name: string
  client_contact_name?: string | null
  client_contact_email?: string | null
}

interface DraftEmail {
  subject: string
  body: string
  recipient_email?: string | null
}

/** GET /api/evidence-status/{id} — the slice this queue reads. */
interface EsDetail {
  obligation_code?: string | null
  object_type?: string | null
  object_id?: string | null
  created_at?: string | null
  obligation?: {
    accepted_sources?: string[] | null
    is_mandatory?: boolean | null
  } | null
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : 'request failed'
}

function taskData(t: TaskQueueItem): Record<string, unknown> {
  return t.task_data && typeof t.task_data === 'object'
    ? (t.task_data as Record<string, unknown>)
    : {}
}

function esIdOf(t: TaskQueueItem): string {
  const v = taskData(t).evidence_status_id
  return v == null ? '' : String(v)
}

/** One owed document, as the queue prints it. */
interface OwedLine {
  code: string | null
  name: string
  cell: ObjectCell | null
  sources: string[]
  optional: boolean
  plug: PlugKind
  plugText: string
  executedCopy: boolean
}

/** One letter: one counterparty of one fund, the rows it asks for. */
interface Letter {
  key: string
  fundId: string
  counterparty: string | null
  rows: DocRequest[]
  overdueDays: number | null
}

type Tab = 'letters' | 'to-request' | 'sent'

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** The letters a send would go out as — draft rows and overdue rows of
 *  the same counterparty ride ONE letter (Ale ruling 2026-08-31). */
function lettersOf(rows: DocRequest[]): Letter[] {
  const groups = new Map<string, DocRequest[]>()
  for (const r of rows) {
    const key = `${r.fund_id}::${normaliseCounterparty(r.counterparty_name) ?? ''}`
    groups.set(key, [...(groups.get(key) ?? []), r])
  }
  return [...groups.entries()].map(([key, group]) => {
    const ages = group
      .filter((r) => r.status === 'overdue')
      .map((r) => ageDays(r.deadline ?? r.sent_at))
      .filter((d): d is number => d != null)
    return {
      key,
      fundId: group[0].fund_id,
      counterparty: normaliseCounterparty(group[0].counterparty_name),
      rows: group,
      overdueDays: ages.length ? Math.max(...ages) : null,
    }
  })
}

export default function ChaseSurface() {
  const [params] = useSearchParams()
  const fundParam = params.get('fund')
  const contactParam = params.get('contact')
  const requestParam = params.get('request')
  const [funds, setFunds] = useState<FundLite[]>([])
  const [requests, setRequests] = useState<DocRequest[]>([])
  const [tasks, setTasks] = useState<TaskQueueItem[]>([])
  const [esDetail, setEsDetail] = useState<Record<string, EsDetail | null>>({})
  /* The ledger rows in scope (B1): the header prints what is OWED — the
   * same figure Today, the fund page and the tile print; the letters
   * drafted are the banner's second clause. */
  const [ledgers, setLedgers] = useState<FundLedger[]>([])
  const [error, setError] = useState<string | null>(null)
  // Loading ≠ empty (state system): the letters read as "nothing owed"
  // only once the first read has landed.
  const [loaded, setLoaded] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<Record<string, DraftEmail | null>>({})
  const [contactEdit, setContactEdit] = useState<string | null>(null)
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [inceptionOpen, setInceptionOpen] = useState(false)
  const [picked, setPicked] = useState<Tab | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  /* `background === true` only from the heartbeat below — the idle clock
   * (lib/api.ts) must not move on a timer. */
  const load = useCallback(
    async (background = false) => {
      const bg = background === true
      try {
        const fundList = await apiFetch<FundLite[]>('/api/funds', { background: bg })
        const scoped = fundParam ? fundList.filter((f) => f.id === fundParam) : fundList
        setFunds(scoped)
        const reqLists = await Promise.all(
          scoped.map((f) =>
            apiFetch<DocRequest[]>(`/api/funds/${f.id}/requests`, { background: bg }).catch(
              () => [] as DocRequest[]
            )
          )
        )
        setRequests(reqLists.flat())
        setTasks(await fetchOpenTasks(fundParam, bg))
        const rows = (await fetchFundLedgers(bg)).funds
        setLedgers(fundParam ? rows.filter((r) => r.fund_id === fundParam) : rows)
        setError(null)
      } catch (err) {
        setError(errText(err))
      } finally {
        setLoaded(true)
      }
    },
    [fundParam]
  )

  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 15000)
    return () => clearInterval(t)
  }, [load])

  /* Evidence-status detail per row — the demand code, the accepted
   * sources and the object type behind every request row and every
   * still-to-request task. Fetched once per id, a few at a time. */
  useEffect(() => {
    const ids = new Set<string>()
    for (const r of requests) if (r.evidence_status_id) ids.add(r.evidence_status_id)
    for (const t of tasks)
      if (t.task_type === 'investigation_action' && esIdOf(t)) ids.add(esIdOf(t))
    const missing = [...ids].filter((id) => esDetail[id] === undefined)
    if (!missing.length) return
    let dead = false
    ;(async () => {
      const entries: [string, EsDetail | null][] = []
      for (let i = 0; i < missing.length; i += 6) {
        const slice = missing.slice(i, i + 6)
        const got = await Promise.all(
          slice.map((id) =>
            apiFetch<EsDetail>(`/api/evidence-status/${id}`, { background: true })
              .then((row) => [id, row] as [string, EsDetail | null])
              .catch(() => [id, null] as [string, EsDetail | null])
          )
        )
        entries.push(...got)
      }
      if (!dead) setEsDetail((prev) => ({ ...prev, ...Object.fromEntries(entries) }))
    })()
    return () => {
      dead = true
    }
  }, [requests, tasks, esDetail])

  const fundOf = useMemo(() => new Map(funds.map((f) => [f.id, f])), [funds])

  const openContact = useCallback((fund: FundLite | undefined) => {
    if (!fund) return
    setContactEdit(fund.id)
    setContactName(fund.client_contact_name ?? '')
    setContactEmail(fund.client_contact_email ?? '')
  }, [])

  /* A door from Today ("no contact for <fund> › add") lands here with
   * ?contact=add — open the capture for the scoped fund straight away. */
  useEffect(() => {
    if (contactParam === 'add' && funds.length === 1) openContact(funds[0])
  }, [contactParam, funds, openContact])

  /* A door from Today's request line lands on THE REQUEST, not the queue:
   * ?request=<id> scrolls the LETTER that carries it into view and lights
   * it (batch B, kept over the letters-first fold). */
  useEffect(() => {
    if (!requestParam || requests.length === 0) return
    const el = document.querySelector<HTMLElement>(
      `[data-request-ids~="${CSS.escape(requestParam)}"]`
    )
    if (!el) return
    el.scrollIntoView({ block: 'center' })
    el.classList.add('cn-landed')
    const t = setTimeout(() => el.classList.remove('cn-landed'), 2400)
    return () => clearTimeout(t)
  }, [requestParam, requests])

  /* ── the three tabs, one home per owed document ─────────────────────
   * LETTERS: drafted (never sent) + overdue (a reminder is due) — both
   * ride the counterparty's one letter. SENT: already gone out. STILL TO
   * REQUEST: no request row at all yet. */
  const letters = useMemo(
    () => lettersOf(requests.filter((r) => r.status === 'draft' || r.status === 'overdue')),
    [requests]
  )
  const sentRows = useMemo(
    () =>
      requests.filter(
        (r) => r.status === 'sent' || r.status === 'escalated' || r.status === 'fulfilled'
      ),
    [requests]
  )

  /* Evidence still to REQUEST (no draft yet) + matured instruments. */
  const chaseTasks = tasks.filter((t) => t.task_type === 'investigation_action')
  const matured = tasks.filter((t) => t.task_type === 'matured_unconfirmed')
  /* Counted ⇒ openable: a request_overdue TASK whose request row did
   * not load still renders an actionable reminder line — the count on
   * the queue line may never exceed what the officer can act on. */
  const overdueRequestIds = new Set(requests.filter((r) => r.status === 'overdue').map((r) => r.id))
  const orphanReminders = tasks.filter(
    (t) => t.task_type === 'request_overdue' && !(t.target_id && overdueRequestIds.has(t.target_id))
  )

  const letterCount = letters.length + orphanReminders.length
  const letterDocs = letters.reduce((n, l) => n + l.rows.length, 0) + orphanReminders.length
  /* The queue's owed figure is the fund LEDGER's (B1) — the same one
   * Today, the fund page and the tile print. */
  const owed = useMemo(() => sumLedgers(ledgers).owed, [ledgers])

  /* ── the owed line, from structured data ──────────────────────────── */

  const lineOfTask = (t: TaskQueueItem): OwedLine => {
    const parsed = parseChaseTitle(t.title)
    const td = taskData(t)
    const es = esDetail[esIdOf(t)] ?? null
    const code =
      (typeof td.obligation_code === 'string' && td.obligation_code) ||
      es?.obligation_code ||
      parsed.code ||
      null
    const objectType =
      (typeof td.object_type === 'string' && td.object_type) || es?.object_type || null
    const plug = objectType && objectType !== 'trade' ? null : plugKind(null, parsed.object)
    const optional =
      td.is_mandatory === false ||
      es?.obligation?.is_mandatory === false ||
      parsed.optional ||
      t.priority === 'low'
    return {
      code,
      name: demandName(code, parsed.label),
      cell: objectCell(parsed, objectType, plug),
      sources: sourceChips(es?.obligation?.accepted_sources, code),
      optional,
      plug,
      plugText: plug ? plugLine(plug, tradeParts(parsed.object), parsed.date) : '',
      executedCopy: parsed.kind === 'executed_copy',
    }
  }

  /* A request row carries the obligation row id; the object's name lives
   * on the open task for the same row when one is still in the queue.
   * When it is not, the clause is omitted — never a placeholder. */
  const taskByEs = useMemo(() => {
    const m = new Map<string, TaskQueueItem>()
    for (const t of tasks)
      if (t.task_type === 'investigation_action' && esIdOf(t)) m.set(esIdOf(t), t)
    return m
  }, [tasks])

  const lineOfRequest = (r: DocRequest): OwedLine => {
    const esId = r.evidence_status_id ?? ''
    const twin = esId ? taskByEs.get(esId) : undefined
    if (twin) return lineOfTask(twin)
    const es = esId ? (esDetail[esId] ?? null) : null
    const code = es?.obligation_code ?? null
    return {
      code,
      name: demandName(code, null),
      cell: null,
      sources: sourceChips(es?.obligation?.accepted_sources, code),
      optional: es?.obligation?.is_mandatory === false,
      plug: null,
      plugText: '',
      executedCopy: false,
    }
  }

  const owedSinceOf = (t: TaskQueueItem): string | null => {
    const es = esDetail[esIdOf(t)] ?? null
    return es?.created_at ?? t.created_at ?? null
  }

  const owedSinceOfRequest = (r: DocRequest): string | null => {
    const esId = r.evidence_status_id ?? ''
    const twin = esId ? taskByEs.get(esId) : undefined
    if (twin) return owedSinceOf(twin)
    return esDetail[esId]?.created_at ?? r.created_at ?? null
  }

  const inceptionRows = chaseTasks.filter((t) => lineOfTask(t).plug === 'inception_balance')
  const toRequest = chaseTasks.filter((t) => !inceptionRows.includes(t))

  /** The object cell: a canon-coloured mark + the name, the issuer or
   *  holder on a second line when the title carries one. */
  const objectMark = (cell: ObjectCell) => {
    switch (cell.kind) {
      case 'trade':
        return <span className="cn-tarr tp">➜</span>
      case 'position':
        return <span className="cn-tdot c-pos" />
      case 'instrument':
        return <span className="cn-tdot c-ins" />
      default:
        return <span className="cn-tdot c-ent" />
    }
  }
  const objectClass = (cell: ObjectCell) =>
    cell.kind === 'trade' || cell.kind === 'position'
      ? 't-posink'
      : cell.kind === 'instrument'
        ? 't-ins'
        : 't-ent'

  const owedLine = (line: OwedLine, testId: string, key: string, right: ReactNode) => (
    <div key={key} className="cn-cl cn-owed-line" data-testid={testId} data-code={line.code ?? ''}>
      <span className="cn-dcode" data-testid="chase-demand-code">
        {line.code ?? '—'}
      </span>
      <span className="cn-dname">
        {line.executedCopy ? `executed copy · ${line.name}` : line.name}
        {line.optional ? (
          <span className="cn-tag" data-testid="chase-optional-tag">
            optional
          </span>
        ) : null}
      </span>
      {line.cell ? (
        <span className="cn-obj" data-testid="chase-object" data-kind={line.cell.kind}>
          <span className={`head ${objectClass(line.cell)}`}>
            {objectMark(line.cell)} {line.cell.head}
            {line.plug ? (
              <span className="cn-tag plug" data-testid="chase-plug-tag" data-plug={line.plug}>
                {line.plug === 'missing_movement' ? 'missing movement' : 'inception balance'}
              </span>
            ) : null}
          </span>
          {line.plug ? (
            <span className="sub" data-testid="chase-plug-line">
              {line.plugText}
            </span>
          ) : line.cell.sub ? (
            <span className="sub">{line.cell.sub}</span>
          ) : null}
        </span>
      ) : (
        <span className="cn-obj" />
      )}
      <span className="cn-srcs" data-testid="chase-sources">
        {line.sources.map((s) => (
          <span key={s} className="cn-src">
            {s}
          </span>
        ))}
      </span>
      <span className="right">{right}</span>
    </div>
  )

  const agePill = (iso: string | null | undefined, overdue: boolean) => {
    const d = ageDays(iso)
    if (d == null) return null
    return (
      <span className={`cn-age${overdue ? '' : ' ok'}`} data-testid="chase-age">
        {d} d
      </span>
    )
  }

  const sinceCell = (word: string, iso: string | null | undefined) => {
    const d = formatOfficerDate(iso)
    return d ? (
      <span className="cn-since" data-testid="chase-since">
        {word} {d}
      </span>
    ) : null
  }

  /** The right-hand cell of one letter row: what was asked and when. */
  const rowState = (r: DocRequest) => {
    if (r.status === 'overdue')
      return (
        <>
          {sinceCell('requested', r.sent_at)}
          {agePill(r.deadline ?? r.sent_at, true)}
        </>
      )
    const since = owedSinceOfRequest(r)
    if (!formatOfficerDate(since))
      return (
        <span className="cn-since" data-testid="chase-since">
          not yet requested
        </span>
      )
    return (
      <>
        {sinceCell('owed since', since)}
        {agePill(since, false)}
      </>
    )
  }

  /* ── actions ──────────────────────────────────────────────────────── */

  const togglePreview = async (key: string, r: DocRequest) => {
    if (preview[key] !== undefined) {
      setPreview((p) => {
        const next = { ...p }
        delete next[key]
        return next
      })
      return
    }
    setPreview((p) => ({ ...p, [key]: null }))
    try {
      const d = await apiFetch<DraftEmail>(`/api/requests/${r.id}/draft-email`)
      setPreview((p) => ({ ...p, [key]: d }))
    } catch {
      setPreview((p) => ({ ...p, [key]: { subject: '', body: 'preview unavailable' } }))
    }
  }

  /** ONE letter goes out: the drafted rows through the batch compose
   *  scoped to exactly them (one counterparty, one letter), the overdue
   *  rows as the reminder on the same letter. */
  const sendOne = async (letter: Letter) => {
    const draftIds = letter.rows.filter((r) => r.status === 'draft').map((r) => r.id)
    const overdue = letter.rows.filter((r) => r.status === 'overdue')
    if (draftIds.length)
      await apiFetch(`/api/funds/${letter.fundId}/requests/batch-send`, {
        method: 'POST',
        body: JSON.stringify({ request_ids: draftIds }),
      })
    for (const r of overdue)
      await apiFetch(`/api/requests/${r.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'escalated' }),
      })
  }

  const sendLetter = async (letter: Letter) => {
    setBusy(true)
    setError(null)
    try {
      await sendOne(letter)
      setNote(
        `✓ sent — one letter, ${plural(letter.rows.length, 'document', 'documents')} asked for`
      )
      load()
    } catch (err) {
      setError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  const sendAll = async () => {
    setBusy(true)
    setError(null)
    try {
      for (const letter of letters) await sendOne(letter)
      setSheetOpen(false)
      setNote(
        letters.length
          ? `✓ sent — ${plural(letters.length, 'letter', 'letters')}, one per counterparty`
          : 'No letters to send.'
      )
      load()
    } catch (err) {
      setError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  const saveContact = async (fund: FundLite) => {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/funds/${fund.id}/contact`, {
        method: 'PATCH',
        body: JSON.stringify({
          client_contact_name: contactName.trim() || null,
          client_contact_email: contactEmail.trim() || null,
        }),
      })
      setContactEdit(null)
      load()
    } catch (err) {
      setError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  const draftRequests = async (rows: TaskQueueItem[]) => {
    const ids = rows.map(esIdOf).filter(Boolean)
    if (!ids.length) return
    setBusy(true)
    setError(null)
    try {
      for (const esId of ids)
        await apiFetch(`/api/evidence-status/${esId}/request`, { method: 'POST' })
      setNote(
        ids.length === 1
          ? '✓ request drafted — it joins its counterparty’s letter'
          : `✓ ${ids.length} requests drafted — each joins its counterparty’s letter`
      )
      load()
    } catch (err) {
      setError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  /* ── blocks ───────────────────────────────────────────────────────── */

  /** The fix-in-place capture. It opens on the door, and it stands open
   *  by itself only where it BLOCKS something — a letter of this fund
   *  that cannot go out without a contact. */
  const contactBlock = (fund: FundLite | undefined, blocked = false) => {
    if (!fund) return null
    const hasContact = !!fund.client_contact_email
    if (contactEdit !== fund.id && (hasContact || !blocked)) return null
    return (
      <div className="cn-addr degraded" data-testid="chase-no-contact">
        <span>
          {hasContact ? (
            <b>Update the client contact for {fund.name}.</b>
          ) : (
            <b>
              No client contact stored for {fund.name}. Who do chase letters go to? A distribution
              address is fine.
            </b>
          )}{' '}
          <input
            aria-label="Contact name"
            placeholder="name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            style={{
              border: '1px solid var(--cn-line)',
              borderRadius: 6,
              padding: '2px 8px',
              font: 'inherit',
              fontSize: 'var(--text-12)',
            }}
          />{' '}
          <input
            aria-label="Contact email"
            placeholder="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            style={{
              border: '1px solid var(--cn-line)',
              borderRadius: 6,
              padding: '2px 8px',
              font: 'inherit',
              fontSize: 'var(--text-12)',
            }}
          />{' '}
          <button
            type="button"
            className="cn-btn"
            style={{ padding: '2px 10px', fontSize: 'var(--text-12)' }}
            disabled={busy}
            onClick={() => saveContact(fund)}
            data-testid="chase-save-contact"
          >
            Save
          </button>
        </span>
      </div>
    )
  }

  const contactDoor = (fund: FundLite | undefined, words: string, testId: string) => (
    <button
      type="button"
      className="cn-door"
      data-testid={testId}
      onClick={() => openContact(fund)}
      disabled={!fund}
    >
      {words} › add
    </button>
  )

  /** The counterparties a fund's letters name — the strip prints one, and
   *  the still-to-request group names it in "Draft all for …" only when
   *  the fund has exactly one on record. Never invented. */
  const counterpartiesOf = (fundId: string): string[] => {
    const names = new Set<string>()
    for (const r of requests)
      if (r.fund_id === fundId) {
        const n = normaliseCounterparty(r.counterparty_name)
        if (n) names.add(n)
      }
    return [...names]
  }

  const recipientOf = (fundId: string, counterparty: string | null): string | null => {
    for (const r of requests)
      if (
        r.fund_id === fundId &&
        normaliseCounterparty(r.counterparty_name) === counterparty &&
        r.recipient_email
      )
        return r.recipient_email
    return null
  }

  /* The fund-level contact strip — said ONCE per fund: who the chases go
   * to, and the counterparty behind the letters. */
  const contactStrip = (fund: FundLite) => {
    const names = counterpartiesOf(fund.id)
    const only = names.length === 1 ? names[0] : null
    const mail = only ? recipientOf(fund.id, only) : null
    return (
      <div key={fund.id}>
        <div className="cn-strip" data-testid="chase-contact-strip" data-fund={fund.id}>
          <b>{fund.name}</b>
          <span className="sep">·</span>
          {fund.client_contact_email ? (
            <>
              chases go to <b>{fund.client_contact_name ?? 'client contact'}</b>{' '}
              <span className="cn-mail">{`<${fund.client_contact_email}>`}</span>
            </>
          ) : (
            <>chases go to {contactDoor(fund, 'no contact stored', 'chase-contact-door')}</>
          )}
          {only ? (
            <>
              <span className="sep">·</span>
              counterparty <b>{only}</b>
              {mail ? <span className="cn-mail">{` <${mail}>`}</span> : null}
            </>
          ) : names.length > 1 ? (
            <>
              <span className="sep">·</span>
              {plural(names.length, 'counterparty', 'counterparties')}
            </>
          ) : null}
          {fund.client_contact_email ? (
            <button
              type="button"
              className="cn-door right"
              data-testid="chase-contact-edit"
              onClick={() => {
                if (contactEdit === fund.id) setContactEdit(null)
                else openContact(fund)
              }}
            >
              edit ›
            </button>
          ) : null}
        </div>
        {contactBlock(
          fund,
          letters.some((l) => l.fundId === fund.id)
        )}
      </div>
    )
  }

  /* ── the letter card ──────────────────────────────────────────────── */

  const letterCard = (letter: Letter) => {
    const fund = fundOf.get(letter.fundId)
    const noContact = !fund?.client_contact_email
    const recipient = recipientOf(letter.fundId, letter.counterparty)
    const pv = preview[letter.key]
    const n = letter.rows.length
    return (
      <div
        key={letter.key}
        data-testid="chase-letter-card"
        data-overdue={!!letter.overdueDays}
        data-request-ids={letter.rows.map((r) => r.id).join(' ')}
      >
        <div className="cn-back" />
        <div className="cn-card" style={{ marginBottom: 20 }}>
          <div className="cn-ctit">
            <span className="k chase">Letter</span>
            <span className="cn-lead" data-testid="chase-letter-lead">
              {letter.counterparty ? (
                <b>{letter.counterparty}</b>
              ) : (
                contactDoor(fund, 'no counterparty stored', 'chase-counterparty-door')
              )}
              {' — '}
              {plural(n, 'document', 'documents')} owed
              {letter.overdueDays != null ? (
                <span className="cn-age" data-testid="chase-letter-overdue">
                  overdue {letter.overdueDays} d
                </span>
              ) : null}
            </span>
            <span className="cn-cta">
              <button
                type="button"
                className="cn-opensrc"
                onClick={() => togglePreview(letter.key, letter.rows[0])}
                data-testid="chase-preview-letter"
              >
                Preview letter ✉
              </button>
              <button
                type="button"
                className="cn-btn p chase"
                disabled={busy || noContact}
                onClick={() => sendLetter(letter)}
                data-testid="chase-send-letter"
              >
                {noContact
                  ? 'Send — needs a contact'
                  : `Send — ${plural(n, 'document', 'documents')}`}
              </button>
            </span>
          </div>
          {letter.rows.map((r) =>
            owedLine(lineOfRequest(r), 'chase-request-row', r.id, rowState(r))
          )}
          <div className="cn-addr">
            <span>
              to:{' '}
              <b>
                {fund?.client_contact_name ?? 'client contact'}
                {fund?.client_contact_email ? ` <${fund.client_contact_email}>` : ''}
              </b>{' '}
              · please obtain from{' '}
              {letter.counterparty ? (
                <>
                  <b>{letter.counterparty}</b>
                  {recipient ? ` <${recipient}>` : ''}
                </>
              ) : (
                contactDoor(fund, 'no counterparty stored', 'chase-counterparty-door')
              )}
            </span>
          </div>
          {pv !== undefined ? (
            <div className="cn-preview" data-testid="chase-letter-preview">
              {pv === null ? (
                'composing…'
              ) : (
                <>
                  {pv.subject ? <b>{pv.subject}</b> : null}
                  <div style={{ whiteSpace: 'pre-wrap' }}>{pv.body}</div>
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  /* A reminder whose request row did not load: the letter is still one
   * click, the line says what the engine knows. */
  const orphanCard = (t: TaskQueueItem) => (
    <div key={t.task_id} data-testid="chase-letter-card" data-overdue="true">
      <div className="cn-back" />
      <div className="cn-card" style={{ marginBottom: 20 }}>
        <div className="cn-ctit">
          <span className="k chase">Letter</span>
          <span className="cn-lead" data-testid="chase-letter-lead">
            <b>{t.title}</b> — 1 document owed
            <span className="cn-age">overdue — reminder due</span>
          </span>
          <span className="cn-cta">
            <button
              type="button"
              className="cn-btn p chase"
              disabled={busy || !t.target_id}
              data-testid="chase-send-letter"
              onClick={async () => {
                if (!t.target_id) return
                setBusy(true)
                try {
                  await apiFetch(`/api/requests/${t.target_id}/status`, {
                    method: 'PATCH',
                    body: JSON.stringify({ status: 'escalated' }),
                  })
                  setNote('✓ sent — one letter, 1 document asked for')
                  load()
                } catch (err) {
                  setError(errText(err))
                } finally {
                  setBusy(false)
                }
              }}
            >
              Send — 1 document
            </button>
          </span>
        </div>
      </div>
    </div>
  )

  /* ── still to request: one group per fund ─────────────────────────── */

  const toRequestGroups = useMemo(() => {
    const groups = new Map<string, TaskQueueItem[]>()
    for (const t of toRequest) groups.set(t.fund_id, [...(groups.get(t.fund_id) ?? []), t])
    return [...groups.entries()]
  }, [toRequest])

  const groupLead = (fundId: string, rows: TaskQueueItem[]) => {
    const fund = fundOf.get(fundId)
    const name = fund?.name ?? rows[0]?.fund_name ?? 'fund'
    const names = counterpartiesOf(fundId)
    const only = names.length === 1 ? names[0] : null
    return (
      <div className="cn-lead cn-group-lead" data-testid="chase-group-lead">
        <b>{name}</b> · owes {plural(rows.length, 'document', 'documents')} ·{' '}
        {fund?.client_contact_email ? (
          <>
            {fund.client_contact_name ? `${fund.client_contact_name} ` : ''}
            <span className="cn-mail">{`<${fund.client_contact_email}>`}</span>
          </>
        ) : (
          contactDoor(fund, 'no contact stored', 'chase-contact-door')
        )}
        <button
          type="button"
          className="cn-btn right"
          disabled={busy || !rows.some((t) => esIdOf(t))}
          onClick={() => draftRequests(rows)}
          data-testid="chase-draft-all"
        >
          {only ? `Draft all for ${only}` : `Draft all — ${rows.length}`}
        </button>
      </div>
    )
  }

  /* ── sent: the letters already out ────────────────────────────────── */

  const sentLetters = useMemo(() => {
    const groups = new Map<string, DocRequest[]>()
    for (const r of sentRows) {
      const day = (r.sent_at ?? r.created_at ?? '').slice(0, 10)
      const key = `${r.fund_id}::${normaliseCounterparty(r.counterparty_name) ?? ''}::${day}`
      groups.set(key, [...(groups.get(key) ?? []), r])
    }
    return [...groups.entries()]
  }, [sentRows])

  const sentState = (r: DocRequest) => {
    if (r.status === 'fulfilled') return 'fulfilled'
    if (r.status === 'escalated') return 'escalated'
    return formatOfficerDate(r.sent_at) ? `requested ${formatOfficerDate(r.sent_at)}` : 'requested'
  }

  /* ── the tab bar ──────────────────────────────────────────────────── */

  const openTab: Tab =
    picked ??
    (letterCount
      ? 'letters'
      : toRequest.length
        ? 'to-request'
        : sentLetters.length
          ? 'sent'
          : 'letters')

  const TAB_LABEL: Record<Tab, string> = {
    letters: 'Letters',
    'to-request': 'Still to request',
    sent: 'Sent',
  }
  const TAB_N: Record<Tab, number> = {
    letters: letterCount,
    'to-request': toRequest.length,
    sent: sentLetters.length,
  }
  const TAB_SENTENCE: Record<Tab, string> = {
    letters:
      'One letter per counterparty. Sending asks the client contact to obtain every document listed from them.',
    'to-request':
      'Owed, never asked for. Drafting adds a document to its counterparty’s letter — nothing goes out until you send it.',
    sent: 'Already out: what each letter asked for, when it went, and who received it.',
  }
  const TAB_ID: Record<Tab, string> = {
    letters: 'letters',
    'to-request': 'to-request',
    sent: 'sent',
  }

  const owedTotal =
    letterDocs + toRequest.length + sentRows.filter((r) => r.status !== 'fulfilled').length
  const overdueTotal =
    requests.filter((r) => r.status === 'overdue').length + orphanReminders.length

  const nothing =
    letterCount === 0 &&
    !toRequest.length &&
    !matured.length &&
    !inceptionRows.length &&
    !sentLetters.length

  return (
    <div className="cnv" data-testid="chase-surface">
      {funds.map((f) => contactStrip(f))}
      {note ? <div className="cn-note">{note}</div> : null}
      {error ? <div className="cn-errline">{error}</div> : null}

      {/* 4A (Ale 2026-09-04): a tab is its label + count; the open tab's
          sentence shows ONCE under the bar. The fund tabs' own pattern —
          aria-current, never a bare role="tab" without a panel. */}
      <div className="cn-tabs">
        {(['letters', 'to-request', 'sent'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            aria-current={openTab === t ? 'page' : undefined}
            className={`cn-tab${openTab === t ? ' on' : ''}`}
            onClick={() => setPicked(t)}
            data-testid={`chase-tab-${TAB_ID[t]}`}
          >
            {TAB_LABEL[t]} <span className="n">{TAB_N[t]}</span>
          </button>
        ))}
        <span className="cn-counts right" data-testid="chase-counts">
          {/* the OWED figure is the fund ledger's (B1) — the same one Today,
              the fund page and the tile print; the queue's own tally is the
              fallback until the ledger reads. */}
          <b data-testid="chase-owed">{owed.total || owedTotal} owed</b> · {letterCount} ready ·{' '}
          {overdueTotal} overdue
        </span>
      </div>
      <p className="cn-tab-sentence" data-testid="chase-tab-sentence">
        {TAB_SENTENCE[openTab]}
      </p>

      {!loaded && nothing ? <Skeleton kind="cards" n={2} height={120} /> : null}

      {openTab === 'letters' ? (
        <div data-testid="chase-letters">
          {letters.map((l) => letterCard(l))}
          {orphanReminders.map((t) => orphanCard(t))}
          {loaded && letterCount === 0 ? (
            <EmptyState
              sentence="Nothing to send — no letter is waiting on you."
              door={{ label: 'Funds ›', to: '/v2/funds' }}
              testId="chase-empty"
            />
          ) : null}
          {letterCount ? (
            <div className="cn-batch">
              <div className="cn-sendall" data-testid="chase-batch">
                <span>
                  <b>
                    Send all — {plural(letters.length, 'letter', 'letters')} ·{' '}
                    {plural(letterDocs, 'document', 'documents')}
                  </b>
                  <small>you will see recipients before sending</small>
                </span>
                <button
                  type="button"
                  className="cn-btn p chase"
                  disabled={busy || letters.length === 0}
                  onClick={() => setSheetOpen(true)}
                  data-testid="send-all-drafts"
                >
                  Send all
                </button>
              </div>
            </div>
          ) : null}
          {sheetOpen ? (
            <div
              className="cn-sheet"
              role="dialog"
              aria-label="Recipients"
              data-testid="chase-send-sheet"
            >
              <div className="cn-sheet-head">
                <b>Recipients</b> — {plural(letters.length, 'letter', 'letters')} ·{' '}
                {plural(letterDocs, 'document', 'documents')}
              </div>
              {letters.map((l) => {
                const fund = fundOf.get(l.fundId)
                return (
                  <div className="cn-sheet-row" key={l.key} data-testid="chase-sheet-recipient">
                    to:{' '}
                    {fund?.client_contact_email ? (
                      <>
                        <b>{fund.client_contact_name ?? 'client contact'}</b>{' '}
                        <span className="cn-mail">{`<${fund.client_contact_email}>`}</span>
                      </>
                    ) : (
                      <b>no contact stored — this letter will not go out</b>
                    )}
                    <span className="sep">·</span>
                    {l.counterparty ? (
                      <>
                        please obtain from <b>{l.counterparty}</b>
                      </>
                    ) : (
                      'no counterparty stored'
                    )}
                    <span className="sep">·</span>
                    {plural(l.rows.length, 'document', 'documents')}
                  </div>
                )
              })}
              <div className="cn-abar">
                <button
                  type="button"
                  className="cn-btn"
                  onClick={() => setSheetOpen(false)}
                  data-testid="chase-send-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cn-btn p chase"
                  disabled={busy}
                  onClick={sendAll}
                  data-testid="chase-send-confirm"
                >
                  Send {plural(letters.length, 'letter', 'letters')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {openTab === 'to-request' ? (
        <div data-testid="chase-still-to-request">
          {toRequestGroups.map(([fundId, rows]) => (
            <div key={fundId} className="cn-group" data-testid="chase-to-request-group">
              {groupLead(fundId, rows)}
              {rows.map((t) => {
                const since = owedSinceOf(t)
                return owedLine(
                  lineOfTask(t),
                  'chase-to-request',
                  t.task_id,
                  <>
                    {sinceCell('owed since', since)}
                    {agePill(since, false)}
                    <button
                      type="button"
                      className="cn-btn"
                      disabled={busy}
                      onClick={() => draftRequests([t])}
                      data-testid="chase-draft-request"
                    >
                      Draft
                    </button>
                  </>
                )
              })}
            </div>
          ))}

          {matured.length ? (
            <>
              <div className="cn-note" style={{ margin: '14px 0 6px' }}>
                Matured instruments — past maturity, positions still live, no closure document. A
                chase, never an automatic close:
              </div>
              {matured.map((t) => (
                <div key={t.task_id} className="cn-cl" data-testid="chase-matured">
                  <span className="cn-tdot c-ins" />
                  <span className="txt">
                    <b>{t.title}</b>
                  </span>
                  <span className="right">
                    <span className="cn-age">owed since maturity</span>
                  </span>
                </div>
              ))}
            </>
          ) : null}

          {inceptionRows.length ? (
            <div className="cn-group inception" data-testid="chase-inception-group">
              <button
                type="button"
                className="cn-fold"
                data-testid="chase-inception-toggle"
                aria-expanded={inceptionOpen}
                onClick={() => setInceptionOpen((o) => !o)}
              >
                {inceptionOpen ? '▾' : '▸'} Inception balances — not requested (
                {inceptionRows.length})
              </button>
              {inceptionOpen ? (
                <>
                  <div className="cn-note" style={{ margin: '2px 0 4px' }}>
                    A starting line, not a movement: it never goes to the counterparty. Its evidence
                    is the balance document that states it.
                  </div>
                  {inceptionRows.map((t) => {
                    const since = owedSinceOf(t)
                    return owedLine(
                      lineOfTask(t),
                      'chase-inception-row',
                      t.task_id,
                      <>
                        {sinceCell('on record since', since)}
                        {agePill(since, false)}
                      </>
                    )
                  })}
                </>
              ) : null}
            </div>
          ) : null}

          {loaded && !toRequest.length && !matured.length && !inceptionRows.length ? (
            <EmptyState
              sentence="Nothing left to ask for — every owed document is on a letter."
              door={{ label: 'Funds ›', to: '/v2/funds' }}
              testId="chase-empty-to-request"
            />
          ) : null}
        </div>
      ) : null}

      {openTab === 'sent' ? (
        <div data-testid="chase-sent">
          {sentLetters.map(([key, rows]) => {
            const fund = fundOf.get(rows[0].fund_id)
            const counterparty = normaliseCounterparty(rows[0].counterparty_name)
            const recipient = rows.map((r) => r.recipient_email).find(Boolean) ?? null
            const day = formatOfficerDate(rows[0].sent_at)
            return (
              <div key={key} className="cn-group" data-testid="chase-sent-letter">
                <div className="cn-lead cn-group-lead" data-testid="chase-sent-lead">
                  {day ? <>Sent {day} · </> : null}
                  {counterparty ? <b>{counterparty}</b> : 'no counterparty stored'}
                  {' — '}
                  {plural(rows.length, 'document', 'documents')} asked for · to:{' '}
                  <b>{fund?.client_contact_name ?? 'client contact'}</b>
                  {fund?.client_contact_email ? (
                    <span className="cn-mail">{` <${fund.client_contact_email}>`}</span>
                  ) : null}
                  {recipient ? (
                    <>
                      {' · '}
                      <span className="cn-mail">{recipient}</span>
                    </>
                  ) : null}
                </div>
                {rows.map((r) =>
                  owedLine(
                    lineOfRequest(r),
                    'chase-sent-row',
                    r.id,
                    <span className="cn-since" data-testid="chase-since">
                      {sentState(r)}
                    </span>
                  )
                )}
              </div>
            )
          })}
          {loaded && !sentLetters.length ? (
            <EmptyState
              sentence="No letter has gone out yet."
              door={{ label: 'Funds ›', to: '/v2/funds' }}
              testId="chase-empty-sent"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
